import { useState } from 'react';
import {
  LinkBase,
  useCanAccess,
  useCreatePath,
  useNotify,
  useRecordContext,
} from 'ra-core';
import { creerUnOrgadmin } from '@groupe-trousseau/shared/api-client';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { deballer, lireLErreur } from '../../fournisseurs/client';
import { oublier } from '../../fournisseurs/curseurs';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **DÉSIGNER LA PERSONNE QUI TIENT L'ESPACE D'UNE STRUCTURE — `CDC-1` F-02.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `POST /api/v1/structures/:id/orgadmins` est livrée depuis le **LOT-1**, et **aucune
 * surface ne l'appelait**. Elle est ouverte au compte qui voit tout **et au secret d'appel**
 * d'une structure — donc le CRM peut la déclencher —, mais **aucun écran ne l'offrait**, et
 * c'est **le seul chemin pour qu'une structure retrouve quelqu'un après une anonymisation**
 * (F-02, cas de bord 3). Une structure dont le dernier `orgadmin` a été anonymisé n'avait
 * ainsi **aucun geste humain** pour reprendre son espace.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LES QUATRE RÈGLES DE F-02, ET TROIS D'ENTRE ELLES SONT DES BORNES DU SERVEUR.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * 1. « **Le compte qui voit tout crée les `orgadmin`, plusieurs par structure** (Q11, Q12).
 *    **Une structure ne peut pas s'en ajouter un elle-même.** » La matrice porte donc
 *    `structures['creer-un-orgadmin']` à `SUPERADMIN` **seul** — la cellule était **déjà
 *    écrite** avant ce bloc, ce qui rend la ligne relisible contre §6.14 sans rien y ajouter.
 *    Le pluriel compte : **ce formulaire ne se ferme pas après un premier succès**, et il ne
 *    demande jamais s'il en existe déjà un.
 * 2. « Un `orgadmin` porte **AU PLUS UN RATTACHEMENT**. **Aucun autre rôle du produit ne
 *    porte cette contrainte** (Q534). » Le refus arrive nommé, `ORGADMIN_RATTACHEMENT_UNIQUE`,
 *    et il est branché plus bas : c'est le seul code de cette route qui décrive une règle
 *    plutôt qu'une panne, donc le seul que l'écran doive traduire.
 * 3. « **Une même personne ne tient pas l'espace de deux structures.** Il lui faut deux
 *    comptes et **deux adresses distinctes**, que le produit ne relie jamais (Q534). » C'est
 *    la conséquence lisible de la règle 2, et le formulaire la dit **avant** la saisie de
 *    l'adresse : la découvrir par un `409` ferait passer une règle du produit pour une panne.
 * 4. « **L'identifiant externe de son rattachement est vide**, et le champ est donc facultatif
 *    au modèle (Q533). » **D'où l'écart le plus visible avec `CreationDeCompte`** : ce
 *    formulaire porte **trois champs**, pas cinq. Ni rôle — il est dans le nom du geste —, ni
 *    identifiant chez la structure — la route n'en prend aucun, et le contrat le confirme :
 *    son corps est `{ nom, prenom, email }`, liste fermée.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE BLOC VIT SUR LA FICHE D'UNE STRUCTURE, ET IL Y VIT POUR LA MÊME RAISON QUE LES
 * TROIS AUTRES.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * La route s'adresse à **une structure par son identifiant** : le geste porte sur celle qu'on
 * regarde, jamais sur la structure courante d'un sélecteur — même raison que `Exports`, « on
 * exporte ce qu'on regarde ». Il n'y a donc **pas d'écran nouveau**, et `structures/index.ts`
 * ne l'exporte pas : F-58 le dit du secret d'appel — « **ce n'est pas un écran à part, c'est
 * une partie de la fiche d'une structure** » —, et l'exposer donnerait un second chemin à un
 * geste que Q566 veut unique.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LA RÉPONSE EST CELLE DU PROVISIONNEMENT, ET TROIS DE SES SIX CHAMPS NE DISENT RIEN
 * ICI. L'ÉCRAN N'EN MONTRE DONC QUE TROIS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Le contrat rend `{ identifiantSwype, rattachementCree, compteDejaExistant, rejeu,
 * relationCreee, cours }` — la forme entière de `provisionnerUnCompte`. **Lus dans le
 * contrat avant d'écrire cet écran :**
 *
 * - `relationCreee` et `cours` **ne peuvent pas valoir autre chose que `false` et vide** : le
 *   corps de cette route ne porte ni `relationAvec` ni `cours`, donc rien ne les déclenche.
 *   Les afficher inventerait une information que le geste ne produit pas.
 * - `identifiantSwype`, `rattachementCree`, `compteDejaExistant` et `rejeu` disent, eux, ce
 *   qui s'est passé, et l'écran les dit — **`rejeu` compris** : « c'est un succès, jamais une
 *   erreur », et sans lui un second envoi de trente secondes passerait pour une seconde
 *   création.
 *
 * **`compteDejaExistant` est le champ qui sert le cas de bord 3**, celui de l'anonymisation :
 * l'adresse est la clé de reconnaissance, donc redésigner une personne connue **retrouve son
 * compte** au lieu d'en créer un second. L'écran le dit, sans quoi « aucun second compte »
 * se lirait comme un échec.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **AUCUN `useRefresh()` APRÈS LE SUCCÈS, ET C'EST UNE DÉCISION, PAS UN OUBLI.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Le patron du dossier finit par `useRefresh()` ou `refetch()`. **Ici il coûterait une ligne
 * de trace que personne n'a demandée** : F-56 pose que « **l'ouverture de la fiche d'une
 * structure laisse une ligne de trace** », et `useRefresh` relit précisément cette fiche.
 * **Et il n'y aurait rien à y voir** : la fiche porte le nom interne, la date de création, le
 * secret, les colonnes d'affichage et les exports — **aucun de ces champs ne bouge** quand un
 * `orgadmin` est désigné.
 *
 * **Ce qui change vraiment est la liste des comptes**, où la personne entre par son
 * rattachement : les curseurs de cette ressource sont donc oubliés, exactement comme
 * `CreationDeCompte` le fait après avoir rattaché — « la ligne neuve est en tête, et les
 * garder ferait sauter ou répéter des lignes ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE L'ÉCRAN N'AFFIRME PAS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **Pas d'email annoncé comme parti.** Même motif que `CreationDeCompte` : le corps rendu ne
 * porte rien sur l'envoi, et l'échec d'un envoi est absorbé côté serveur. La phrase dit donc
 * la **règle du produit**, jamais un fait constaté.
 *
 * **Aucune liste des `orgadmin` en place.** Le contrat n'en rend aucune par cette route, et
 * les personnes rattachées se lisent à leur écran, la liste des comptes. En fabriquer une ici
 * demanderait une seconde source pour un inventaire qui existe déjà.
 */

/** Ce que l'écran retient d'un succès : quatre champs du contrat, et pas les six. */
type Aboutissement = {
  readonly compteId: string;
  readonly email: string;
  readonly rattachementCree: boolean;
  readonly compteDejaExistant: boolean;
  readonly rejeu: boolean;
};

type Saisie = { prenom: string; nom: string; email: string };

const SAISIE_VIDE: Saisie = { prenom: '', nom: '', email: '' };

export function DesignationDunOrgadmin() {
  const structure = useRecordContext<{ id: string; nomInterne?: string }>();
  const notifier = useNotify();
  const cheminDe = useCreatePath();
  const { canAccess: peutDesigner, isPending: droitEnCours } = useCanAccess({
    resource: 'structures',
    action: 'creer-un-orgadmin',
  });

  const [saisie, setSaisie] = useState<Saisie>(SAISIE_VIDE);
  const [enCours, setEnCours] = useState(false);
  const [abouti, setAbouti] = useState<Aboutissement | null>(null);

  // **Le droit se lit AVANT l'écran**, jamais pendant : `useCanAccess` répond en deux temps,
  // et rendre le formulaire pendant l'attente le ferait clignoter chez qui n'y a pas droit.
  if (droitEnCours) return null;
  if (structure === undefined) return null;
  // **Rien plutôt qu'un refus écrit, et l'écart avec `CreationDeCompte` est voulu** : là-bas
  // l'adresse est atteignable à la main, donc « une absence et un refus se lisent pareil »
  // impose d'écrire le refus. Ici le bloc est une partie d'une fiche que l'`orgadmin` peut
  // ouvrir : lui servir un encart « ce n'est pas votre geste » sur sa propre structure
  // ajouterait du bruit à chaque visite, pour un geste qu'il ne cherchait pas.
  if (!peutDesigner) return null;

  const poser = <C extends keyof Saisie>(champ: C, valeur: string) =>
    setSaisie((precedente) => ({ ...precedente, [champ]: valeur }));

  const designer = async (evenement: React.FormEvent<HTMLFormElement>) => {
    evenement.preventDefault();
    setEnCours(true);
    try {
      const cree = await deballer(
        creerUnOrgadmin({
          path: { id: structure.id },
          body: {
            nom: saisie.nom.trim(),
            prenom: saisie.prenom.trim(),
            email: saisie.email.trim(),
          },
        }),
      );
      setAbouti({
        compteId: cree.identifiantSwype,
        email: saisie.email.trim(),
        rattachementCree: cree.rattachementCree,
        compteDejaExistant: cree.compteDejaExistant,
        rejeu: cree.rejeu,
      });
      // La personne entre dans la liste des comptes de cette structure par son rattachement.
      oublier('comptes');
      setSaisie(SAISIE_VIDE);
    } catch (erreur) {
      const lue = lireLErreur(erreur);
      notifier(messageDuRefus(lue.code, lue.message), { type: 'error' });
    } finally {
      setEnCours(false);
    }
  };

  const complet =
    saisie.prenom.trim() !== '' &&
    saisie.nom.trim() !== '' &&
    saisie.email.trim() !== '';

  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-sm font-medium">
        Désigner une personne qui tient cet espace
      </h3>
      <p className="text-muted-foreground text-sm">
        Une structure ne s’en ajoute pas elle-même : ce geste appartient au
        compte qui voit tout, et c’est le seul chemin pour qu’une structure
        retrouve quelqu’un après une anonymisation. Il peut y en avoir plusieurs
        sur une même structure.
      </p>

      {abouti !== null && (
        <Alert>
          <AlertTitle>
            {abouti.rejeu
              ? 'Cet appel répétait le précédent : rien n’a été créé une seconde fois.'
              : abouti.compteDejaExistant
                ? 'Cette adresse était déjà connue : son compte a été retrouvé, et il tient ' +
                  'désormais cet espace.'
                : 'La personne est créée et elle tient cet espace.'}
          </AlertTitle>
          <AlertDescription>
            <span className="block">
              {abouti.rattachementCree
                ? 'Son rattachement à cette structure est ouvert, et il restera le seul : un ' +
                  'orgadmin en porte au plus un.'
                : 'Aucun rattachement nouveau n’a été ouvert — elle en portait déjà un sur ' +
                  'cette structure.'}
            </span>
            <span className="mt-2 block">
              Le produit fait partir un code d’activation à {abouti.email} —
              cette réponse ne le confirme pas : elle ne porte rien sur l’envoi.
              La personne peut le redemander depuis la page de connexion.
            </span>
            <span className="mt-2 block">
              Son identifiant Swype est{' '}
              <code className="font-mono text-xs">{abouti.compteId}</code>.
            </span>
            <LinkBase
              className={
                buttonVariants({ variant: 'outline', size: 'sm' }) + ' mt-3'
              }
              to={cheminDe({
                resource: 'comptes',
                type: 'show',
                id: abouti.compteId,
              })}
            >
              Ouvrir sa fiche
            </LinkBase>
          </AlertDescription>
        </Alert>
      )}

      <form className="flex flex-col gap-3" onSubmit={(e) => void designer(e)}>
        <div className="flex flex-col gap-1">
          <Label htmlFor="orgadmin-prenom">Prénom</Label>
          <Input
            id="orgadmin-prenom"
            value={saisie.prenom}
            onChange={(e) => poser('prenom', e.target.value)}
            autoComplete="off"
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="orgadmin-nom">Nom</Label>
          <Input
            id="orgadmin-nom"
            value={saisie.nom}
            onChange={(e) => poser('nom', e.target.value)}
            autoComplete="off"
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="orgadmin-email">Adresse email</Label>
          <Input
            id="orgadmin-email"
            type="email"
            value={saisie.email}
            onChange={(e) => poser('email', e.target.value)}
            autoComplete="off"
          />
          <span className="text-muted-foreground text-xs">
            Une même personne ne tient pas l’espace de deux structures : il lui
            faut deux comptes et deux adresses distinctes, que le produit ne
            relie jamais. Une adresse déjà connue retrouve son compte au lieu
            d’en créer un second.
          </span>
        </div>

        <Button
          type="submit"
          disabled={enCours || !complet}
          className="self-start"
        >
          Désigner
        </Button>
        <span className="text-muted-foreground text-xs">
          Trois champs, et pas cinq : le rôle est dans le geste, et
          l’identifiant de la personne chez la structure reste vide — un
          orgadmin n’en porte aucun.
        </span>
      </form>
    </section>
  );
}

/**
 * **Le seul code de cette route qui décrive une règle, et non une panne.**
 *
 * `DOCTRINE.md` §5 : « `code` est le contrat réel […] **jamais `message`**, qui est destiné à
 * l'humain et peut changer sans préavis. » Trois codes sont déclarés au contrat :
 *
 * - `ORGADMIN_RATTACHEMENT_UNIQUE` **est la règle Q534 elle-même**, et son message serveur ne
 *   peut pas dire ce que la personne doit faire à la place. C'est donc le seul traduit ici.
 * - `ACTION_RESERVEE_AU_COMPTE_PARC` refuse l'appel présentant le **secret** d'une structure :
 *   il n'est pas atteignable depuis cette surface, où l'appelant est toujours une personne
 *   connectée. Il est nommé quand même — le taire ferait, le jour où il arrive, une panne sans
 *   cause lisible.
 * - `ROLE_INCOMPATIBLE` porte trois refus différents côté serveur, et **seul son message les
 *   distingue** : le brancher sur son code perdrait précisément ce qui les sépare.
 */
function messageDuRefus(code: string, message: string): string {
  if (code === 'ORGADMIN_RATTACHEMENT_UNIQUE') {
    return (
      'Cette personne tient déjà l’espace d’une structure. Un orgadmin porte au plus un ' +
      'rattachement, et c’est le seul rôle du produit dans ce cas : pour tenir celui-ci, il ' +
      'lui faut un second compte, avec une seconde adresse.'
    );
  }
  if (code === 'ACTION_RESERVEE_AU_COMPTE_PARC') {
    return (
      'Ce geste appartient au compte qui voit tout : une structure ne se désigne pas ' +
      'elle-même une personne qui tient son espace.'
    );
  }
  return message;
}
